// src/components/maps/MapModal.tsx
import React, { useEffect, useMemo } from "react";
import { createPortal } from "react-dom";

type Props = {
  open: boolean;
  onClose: () => void;
  lat: number | string | null | undefined;
  lng: number | string | null | undefined;
  title?: string;
};

function toNum(v: any): number {
  const n = Number(v);
  return Number.isFinite(n) ? n : NaN;
}

function isValidCoord(lat: number, lng: number) {
  return (
    Number.isFinite(lat) &&
    Number.isFinite(lng) &&
    Math.abs(lat) <= 90 &&
    Math.abs(lng) <= 180
  );
}

function buildEmbedUrl(lat: number, lng: number) {
  return `https://maps.google.com/maps?q=${lat},${lng}&z=16&output=embed`;
}

function buildOpenUrl(lat: number, lng: number) {
  return `https://www.google.com/maps/search/?api=1&query=${lat},${lng}`;
}

const MapModal: React.FC<Props> = ({ open, onClose, lat, lng, title }) => {
  const latNum = useMemo(() => toNum(lat), [lat]);
  const lngNum = useMemo(() => toNum(lng), [lng]);
  const canShowMap = useMemo(() => isValidCoord(latNum, lngNum), [latNum, lngNum]);

  // ESC closes
  useEffect(() => {
    if (!open) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [open, onClose]);

  // Lock body scroll when open
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  if (!open) return null;

  return createPortal(
    <div
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 999999,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 16,
      }}
      role="dialog"
      aria-modal="true"
      aria-label="Map"
    >
      {/* Backdrop */}
      <div
        onClick={onClose}
        style={{
          position: "absolute",
          inset: 0,
          background: "rgba(0,0,0,0.5)",
        }}
        aria-hidden="true"
      />

      {/* Modal */}
      <div
        style={{
          position: "relative",
          width: "100%",
          maxWidth: 1100,
          background: "#fff",
          borderRadius: 16,
          overflow: "hidden",
          boxShadow: "0 20px 60px rgba(0,0,0,0.35)",
        }}
      >
        {/* Header */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 12,
            padding: "12px 16px",
            borderBottom: "1px solid #e5e7eb",
          }}
        >
          <div style={{ minWidth: 0, textAlign: "right" }}>
            <div style={{ fontWeight: 800, color: "#111827" }}>الخريطة</div>
            {title ? (
              <div
                style={{
                  fontSize: 12,
                  color: "#6b7280",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  maxWidth: 720,
                }}
                title={title}
              >
                {title}
              </div>
            ) : null}
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <a
              href={canShowMap ? buildOpenUrl(latNum, lngNum) : undefined}
              target="_blank"
              rel="noreferrer"
              style={{
                fontSize: 14,
                fontWeight: 700,
                color: canShowMap ? "#0f766e" : "#d1d5db",
                pointerEvents: canShowMap ? "auto" : "none",
                textDecoration: "none",
              }}
              title={canShowMap ? "فتح في خرائط Google" : "الخريطة غير متاحة"}
            >
              فتح في Google
            </a>

            <button
              type="button"
              onClick={onClose}
              style={{
                height: 36,
                padding: "0 12px",
                borderRadius: 10,
                border: "1px solid #d1d5db",
                background: "#fff",
                fontWeight: 800,
                cursor: "pointer",
              }}
            >
              إغلاق
            </button>
          </div>
        </div>

        {/* Body */}
        {canShowMap ? (
          <div style={{ width: "100%", aspectRatio: "16 / 9" as any }}>
            <iframe
              title="map"
              src={buildEmbedUrl(latNum, lngNum)}
              style={{ width: "100%", height: "100%", border: 0 }}
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
          </div>
        ) : (
          <div style={{ padding: 32, textAlign: "center" }}>
            <div style={{ fontWeight: 800, marginBottom: 8, color: "#111827" }}>
              الخريطة غير متاحة
            </div>
            <div style={{ fontSize: 14, color: "#6b7280" }}>
              لا توجد إحداثيات صحيحة لهذا العنصر.
            </div>
          </div>
        )}
      </div>
    </div>,
    document.body
  );
};

export default MapModal;
